import { Fragment, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  EyeIcon,
  EyeSlashIcon,
  SignalIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  MinusCircleIcon,
  QuestionMarkCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import { Card, CardHeader, Button, Input, Badge, InsurerLogo } from '@/components/common';
import { getErrorMessage } from '@/services';
import { formatDateTime } from '@/utils';
import {
  useInsurerCredentials,
  useRequestActivation,
  useTestInsurerConnection,
  useUpdateInsurerCredentials,
} from '@/hooks/useIntegrations';
import {
  describeTestStage,
  type CredentialFieldSchema,
  type CredentialStatus,
  type IntegrationEnv,
  type InsurerCredentialEnv,
  type InsurerCredentialRow,
  type StoredCredentialField,
} from '@/services/integrations';

const ENV_LABELS: Record<IntegrationEnv, string> = {
  sandbox: 'Sandbox',
  production: 'Production',
};

/** One text value per declared field key. */
type CredentialFormData = Record<string, string>;

function StatusBadge({ status }: { status: CredentialStatus }) {
  if (status === 'connected') {
    return (
      <Badge variant="success" size="sm">
        <CheckCircleIcon className="w-3.5 h-3.5 mr-1" />
        Connected
      </Badge>
    );
  }
  if (status === 'error') {
    return (
      <Badge variant="error" size="sm">
        <ExclamationCircleIcon className="w-3.5 h-3.5 mr-1" />
        Error
      </Badge>
    );
  }
  if (status === 'awaiting_activation') {
    return (
      <Badge variant="warning" size="sm">
        <ClockIcon className="w-3.5 h-3.5 mr-1" />
        Awaiting activation
      </Badge>
    );
  }
  if (status === 'no_adapter') {
    return (
      <Badge variant="default" size="sm">
        <MinusCircleIcon className="w-3.5 h-3.5 mr-1" />
        Not available
      </Badge>
    );
  }
  if (status === 'untested') {
    return (
      <Badge variant="warning" size="sm">
        <QuestionMarkCircleIcon className="w-3.5 h-3.5 mr-1" />
        Saved, untested
      </Badge>
    );
  }
  return (
    <Badge variant="default" size="sm">
      <MinusCircleIcon className="w-3.5 h-3.5 mr-1" />
      Not Configured
    </Badge>
  );
}

/** The form's starting values: stored plain values back in their boxes; secrets always
 *  blank, because they are write-only. */
function defaultsFor(fields: CredentialFieldSchema[], credentials: InsurerCredentialEnv): CredentialFormData {
  const stored = credentials.stored_fields ?? {};
  return Object.fromEntries(
    fields.map((field) => [field.key, field.secret ? '' : (stored[field.key]?.value ?? '')]),
  );
}

/** What the server holds for one field, in words -- never a secret's value. */
function storedText(field: CredentialFieldSchema, entry: StoredCredentialField | undefined): string {
  if (!entry?.stored) return 'not set';
  if (field.secret) return entry.hint ? `stored (${entry.hint})` : 'stored';
  return entry.value ?? 'stored';
}

function placeholderFor(field: CredentialFieldSchema, entry: StoredCredentialField | undefined): string {
  if (!field.secret) return field.required ? `Enter the ${field.label}` : 'Optional';
  if (entry?.stored) {
    return entry.hint ? `Stored (${entry.hint}) — leave blank to keep` : 'Stored — leave blank to keep it';
  }
  return `Not set — enter the ${field.label.toLowerCase()}`;
}

/**
 * What the SERVER holds for one environment -- never the form's own state.
 *
 * Secrets are write-only, so a reopened card has blank secret boxes whether or
 * not anything is stored. Every line here is read from the credential payload.
 */
function StoredCredentials({
  fields,
  credentials,
}: {
  fields: CredentialFieldSchema[];
  credentials: InsurerCredentialEnv;
}) {
  if (!credentials.saved_at) {
    return (
      <div
        data-testid="stored-credentials"
        className="rounded-md border border-dashed border-gray-300 px-3 py-2 text-xs text-gray-500 dark:border-gray-600 dark:text-gray-400"
      >
        Nothing saved yet for this environment.
      </div>
    );
  }

  const stored = 'font-medium text-green-700 dark:text-green-400';
  const missing = 'font-medium text-amber-700 dark:text-amber-400';

  return (
    <div
      data-testid="stored-credentials"
      className="rounded-md border border-green-200 bg-green-50 px-3 py-2 dark:border-green-900 dark:bg-green-950/40"
    >
      <p className="text-xs font-semibold text-green-900 dark:text-green-200">
        Saved {formatDateTime(credentials.saved_at)}
      </p>
      <dl className="mt-1.5 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs text-gray-700 dark:text-gray-300">
        {fields.map((field) => {
          const entry = credentials.stored_fields?.[field.key];
          const className = !entry?.stored ? missing : field.secret ? stored : 'font-mono break-all';
          return (
            <Fragment key={field.key}>
              <dt>{field.label}</dt>
              <dd data-testid={`stored-field-${field.key}`} className={className}>
                {storedText(field, entry)}
              </dd>
            </Fragment>
          );
        })}
        <dt>Last test</dt>
        <dd data-testid="stored-last-test">
          {credentials.last_tested_at
            ? `${formatDateTime(credentials.last_tested_at)} · ${
                credentials.status === 'connected'
                  ? 'passed'
                  : credentials.status === 'error'
                    ? 'failed'
                    : credentials.status.replace(/_/g, ' ')
              }`
            : 'never tested'}
        </dd>
      </dl>
    </div>
  );
}

function EnvCredentialForm({
  insurerId,
  env,
  credentials,
  fields,
}: {
  insurerId: string;
  env: IntegrationEnv;
  credentials: InsurerCredentialEnv;
  fields: CredentialFieldSchema[];
}) {
  const [shown, setShown] = useState<Record<string, boolean>>({});
  const save = useUpdateInsurerCredentials();
  const test = useTestInsurerConnection();
  const requestActivation = useRequestActivation();

  // The server refuses a key until an admin has enabled the integration, so an
  // editable form would only produce a rejection. `no_adapter` is worse: no
  // admin action helps, so there is nothing to ask for either.
  const awaitingActivation = credentials.status === 'awaiting_activation';
  const unavailable = credentials.status === 'no_adapter';
  const locked = awaitingActivation || unavailable;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CredentialFormData>({ defaultValues: defaultsFor(fields, credentials) });

  // Every card renders fields with the same keys, and <Input> falls back to the
  // name for its id -- so ids are scoped per insurer and environment, or a
  // label's `for` points at the first card's input. No whitespace in an id.
  const fieldId = (key: string) => `${insurerId}-${env}-${key}`.replace(/\s+/g, '-');

  // What the LAST test reported. Which leg it stopped at is the server's to
  // say: "could not reach" and "refused this credential" must never look alike.
  const testResult = test.data;
  const testStage = testResult ? describeTestStage(testResult.stage) : null;

  const onSave = (data: CredentialFormData) => {
    // Only what was typed: a blank box means "keep what is stored".
    const typed = Object.fromEntries(
      Object.entries(data)
        .map(([key, value]) => [key, (value ?? '').trim()])
        .filter(([, value]) => value !== ''),
    );
    save.mutate(
      { insurerId, environment: env, data: { fields: typed } },
      // Empty the secret boxes -- the panel above now says what is stored.
      { onSuccess: (saved) => reset(defaultsFor(fields, saved)) },
    );
  };

  if (unavailable) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Not available — this insurer has no integration on the platform yet.
      </p>
    );
  }

  if (fields.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400">
        This insurer declares no credential form yet. Ask your administrator.
      </p>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSave)}>
      <div className="space-y-4">
        {awaitingActivation && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-900 dark:bg-amber-950">
            <p className="text-xs text-amber-800 dark:text-amber-200">
              Your administrator has not enabled this insurer for {ENV_LABELS[env]} yet.
              Request it and they will set the endpoint up.
            </p>
            {credentials.activation_requested ? (
              <p className="mt-2 text-xs font-medium text-amber-900 dark:text-amber-100">
                Activation requested — waiting on your administrator.
              </p>
            ) : (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="mt-2"
                isLoading={requestActivation.isPending}
                onClick={() => requestActivation.mutate({ insurerId, environment: env })}
              >
                Request activation
              </Button>
            )}
          </div>
        )}
        <StoredCredentials fields={fields} credentials={credentials} />
        {save.isSuccess && save.data && (
          <p role="status" data-testid="save-confirmation" className="text-xs text-green-700 dark:text-green-400">
            Saved. The server now holds:{' '}
            {fields.map((field) => `${field.label} ${storedText(field, save.data.stored_fields?.[field.key])}`).join(', ')}.
          </p>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">API Endpoint</label>
          {credentials.base_url ? (
            <p className="text-sm text-gray-900 dark:text-gray-100 font-mono break-all bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md px-3 py-2">
              {credentials.base_url}
            </p>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md px-3 py-2">
              Not set yet — your administrator configures this.
            </p>
          )}
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Set by your administrator and shared across brokers. You only add your own key.
          </p>
        </div>
        {/*
          The fields, labels and help come from the insurer's plug-in on the
          server. `required` is enforced by the validator, never passed to
          <Input>: its "*" would sit inside the <label> and change its text.
        */}
        {fields.map((field) => {
          const entry = credentials.stored_fields?.[field.key];
          const visible = Boolean(shown[field.key]);
          return (
            <Input
              key={field.key}
              label={field.label}
              type={field.secret && !visible ? 'password' : 'text'}
              data-custom-reveal={field.secret ? 'true' : undefined}
              placeholder={placeholderFor(field, entry)}
              helperText={field.help || undefined}
              error={errors[field.key]?.message}
              disabled={locked}
              id={fieldId(field.key)}
              rightIcon={
                field.secret ? (
                  <button
                    type="button"
                    onClick={() => setShown((current) => ({ ...current, [field.key]: !current[field.key] }))}
                    className="cursor-pointer"
                  >
                    {visible ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                  </button>
                ) : undefined
              }
              {...register(field.key, {
                validate: (value) =>
                  locked || !field.required || (value ?? '').trim() !== '' || entry?.stored
                    ? true
                    : `${field.label} is required`,
              })}
            />
          );
        })}

        {testResult && testStage ? (
          <div
            data-testid="test-connection-result"
            className={`rounded-md border px-3 py-2 ${
              testResult.success
                ? 'border-green-200 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-900/20 dark:text-green-300'
                : 'border-red-200 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300'
            }`}
          >
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={testResult.success ? 'success' : 'error'} size="sm">
                {testResult.success ? 'Passed' : 'Failed'}
              </Badge>
              <span data-testid="test-stage" className="font-mono text-[11px]">
                stage: {testStage.stage}
                {testResult.http_status != null ? ` · HTTP ${testResult.http_status}` : ''}
              </span>
            </div>
            <p data-testid="test-stage-label" className="mt-2 text-xs font-medium">
              {testStage.label}
            </p>
            <p data-testid="test-stage-meaning" className="mt-1 text-xs">
              {testStage.meaning}
            </p>
            {testStage.nextStep && <p className="mt-1 text-xs">{testStage.nextStep}</p>}
            {testResult.message && <p className="mt-2 break-all font-mono text-[11px]">{testResult.message}</p>}
          </div>
        ) : (
          credentials.last_test_message && (
            <p
              className={`text-xs ${
                credentials.status === 'error' ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              Last test: {credentials.last_test_message}
            </p>
          )
        )}

        <div className="flex items-center gap-3 pt-1">
          <Button type="submit" size="sm" isLoading={save.isPending} disabled={locked}>
            Save
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            isLoading={test.isPending}
            disabled={locked || !credentials.is_configured}
            leftIcon={<SignalIcon className="w-4 h-4" />}
            onClick={() => test.mutate({ insurerId, environment: env })}
          >
            Test Connection
          </Button>
        </div>
      </div>
    </form>
  );
}

function InsurerCredentialCard({ insurer }: { insurer: InsurerCredentialRow }) {
  const fields = insurer.credential_fields ?? [];
  return (
    <div data-testid={`credential-card-${insurer.insurer_name}`}>
      <Card>
        <div className="flex items-center gap-3 mb-6">
          <InsurerLogo
            src={insurer.logo_url}
            name={insurer.insurer_name}
            description={insurer.insurer_description}
            size="md"
          />
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{insurer.insurer_name}</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">{insurer.insurer_code}</p>
            {insurer.insurer_description && (
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{insurer.insurer_description}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {(['sandbox', 'production'] as const).map((env) => (
            <div key={env} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                  {ENV_LABELS[env]}
                </h4>
                <StatusBadge status={insurer[env].status} />
              </div>
              <EnvCredentialForm insurerId={insurer.insurer_id} env={env} credentials={insurer[env]} fields={fields} />
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

export function BrokerCredentialsPage() {
  const { data, isLoading, error } = useInsurerCredentials();
  const insurers = data?.results ?? [];

  return (
    <div>
      <PageHeader
        title="API Credentials"
        subtitle="Your own insurer API keys, per environment. These are used to fetch quotes and issue policies under your broker code."
        breadcrumbs={[{ label: 'API Credentials' }]}
      />

      {isLoading ? (
        <div className="space-y-4">
          {[...Array(2)].map((_, i) => (
            <Card key={i}>
              <div className="animate-pulse space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded bg-gray-200" />
                  <div className="space-y-1">
                    <div className="h-4 bg-gray-200 rounded w-32" />
                    <div className="h-3 bg-gray-200 rounded w-16" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-6">
                  <div className="h-40 bg-gray-100 rounded-lg" />
                  <div className="h-40 bg-gray-100 rounded-lg" />
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : error ? (
        <Card>
          <CardHeader
            title="Could not load credentials"
            subtitle={
              needsBrokerChoice(error)
                ? 'Your account manages more than one brokerage. Choose which one from the Select Broker menu at the top of the page — credentials belong to one brokerage.'
                : getErrorMessage(error)
            }
          />
        </Card>
      ) : insurers.length === 0 ? (
        <Card>
          <CardHeader
            title="No Insurers Linked"
            subtitle="Your broker is not linked to any insurance company yet. Ask your administrator to add the relationship, then your keys go here."
          />
        </Card>
      ) : (
        <div className="space-y-6">
          {insurers.map((insurer) => (
            <InsurerCredentialCard key={insurer.insurer_id} insurer={insurer} />
          ))}
        </div>
      )}
    </div>
  );
}

/** The server's "which broker?" refusal: a 400 that lists the brokers. */
function needsBrokerChoice(error: unknown): boolean {
  const data = (error as { response?: { status?: number; data?: { brokers?: unknown } } })?.response;
  return data?.status === 400 && Array.isArray(data?.data?.brokers);
}
